import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { 
  ShoppingBag, 
  Euro, 
  Clock, 
  ChevronRight,
  RefreshCw,
  Store,
  AlertTriangle,
  Package,
  Bell,
  BellOff
} from 'lucide-react';
import axios from 'axios';
import OnboardingBanner from '../../components/restaurant/OnboardingBanner';
import {
  isNotificationSupported,
  getNotificationPermission,
  areNotificationsEnabled,
  requestNotificationPermission,
  enableNotifications,
  disableNotifications,
  showNewOrderNotification,
  showMultipleOrdersNotification,
  playNotificationSound
} from '../../services/notificationService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  'Reçue': 'bg-orange-100 text-orange-700 border-orange-200',
  'En préparation': 'bg-blue-100 text-blue-700 border-blue-200',
  'Prête': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Terminée': 'bg-gray-100 text-gray-600 border-gray-200',
  'Annulée': 'bg-red-100 text-red-700 border-red-200',
  'pending_payment': 'bg-yellow-100 text-yellow-700 border-yellow-200',
};

const statusLabel = (status) => status === 'pending_payment' ? 'Paiement en cours' : status;

const nextStatus = {
  'Reçue': 'En préparation',
  'En préparation': 'Prête',
  'Prête': 'Terminée',
  'pending_payment': 'Reçue',
};

const statusActions = {
  'Reçue': 'Commencer la préparation',
  'En préparation': 'Marquer comme prête',
  'Prête': 'Commande récupérée',
  'pending_payment': 'Confirmer le paiement',
};

export default function RestaurantDashboard() {
  useAuth();
  const [data, setData] = useState({ orders: [], stats: { total_orders: 0, total_revenue: 0 } });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const [isOpen, setIsOpen] = useState(true);
  const [togglingOpen, setTogglingOpen] = useState(false);
  const [lowStockDishes, setLowStockDishes] = useState([]);
  const [ordersPaused, setOrdersPaused] = useState(false);
  const [togglingPause, setTogglingPause] = useState(false);
  const [showStockAlerts, setShowStockAlerts] = useState(true);
  const [acceptingOrders, setAcceptingOrders] = useState(true);
  const [acceptingOrdersReason, setAcceptingOrdersReason] = useState(null);
  
  // Notifications state
  const [notificationsEnabled, setNotificationsEnabled] = useState(areNotificationsEnabled());
  const [notificationPermission, setNotificationPermission] = useState(getNotificationPermission());
  const notificationSupported = isNotificationSupported();

  const { isAuthenticated } = useAuth();
  const { subscribe: subscribePush, unsubscribe: unsubscribePush, isSubscribed: pushSubscribed, isSupported: pushSupported } = usePushNotifications(isAuthenticated);

  // Handle notification toggle
  const handleToggleNotifications = async () => {
    if (notificationsEnabled) {
      disableNotifications();
      if (pushSubscribed) await unsubscribePush();
      setNotificationsEnabled(false);
      toast.success('Notifications desactivees');
    } else {
      if (notificationPermission !== 'granted') {
        const result = await requestNotificationPermission();
        setNotificationPermission(getNotificationPermission());
        if (!result.success) {
          if (result.reason === 'denied') {
            toast.error('Les notifications sont bloquees. Activez-les dans les parametres de votre navigateur.');
          }
          return;
        }
      }
      enableNotifications();
      // Also subscribe to push notifications
      if (pushSupported) await subscribePush();
      setNotificationsEnabled(true);
      toast.success('Notifications activees (push + navigateur)');
    }
  };

  const fetchTodayOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/restaurant-dashboard/orders/today`);
      
      // Check for new orders
      setData(prevData => {
        const currentPendingCount = prevData.orders.filter(o => o.status === 'Reçue').length;
        const newPendingCount = response.data.orders.filter(o => o.status === 'Reçue').length;
        
        if (currentPendingCount > 0 && newPendingCount > currentPendingCount) {
          const newCount = newPendingCount - currentPendingCount;
          toast.success(`${newCount} nouvelle(s) commande(s)!`, {
            description: 'Nouvelles commandes en attente',
            duration: 5000,
          });
          
          // Envoyer notification push si activée
          if (areNotificationsEnabled()) {
            const newOrders = response.data.orders
              .filter(o => o.status === 'Reçue')
              .slice(0, newCount);
            
            if (newCount === 1 && newOrders[0]) {
              showNewOrderNotification(newOrders[0], () => window.focus());
            } else {
              showMultipleOrdersNotification(newCount, () => window.focus());
            }
            playNotificationSound();
          }
        }
        
        setNewOrdersCount(newPendingCount);
        return response.data;
      });
      
      setLastUpdate(new Date());
    } catch {
      console.error('Error fetching orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTodayOrders();
    fetchRestaurantStatus();
    // Auto-refresh every 10 seconds for real-time updates
    const interval = setInterval(() => {
      fetchTodayOrders();
      fetchRestaurantStatus();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchTodayOrders]);

  const fetchRestaurantStatus = async () => {
    try {
      const response = await axios.get(`${API}/restaurant-dashboard/settings`);
      setIsOpen(response.data.is_open !== false);
      setOrdersPaused(response.data.orders_paused === true);
      setShowStockAlerts(response.data.show_stock_alerts !== false);
      setAcceptingOrders(response.data.accepting_orders !== false);
      setAcceptingOrdersReason(response.data.accepting_orders_reason || null);
    } catch {
      console.error('Error fetching restaurant status');
    }
  };

  const fetchLowStockDishes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/restaurant-dashboard/menu/low-stock`);
      setLowStockDishes(response.data || []);
    } catch {
      console.error('Error fetching low stock dishes');
    }
  }, []);

  useEffect(() => {
    fetchLowStockDishes();
  }, [fetchLowStockDishes]);

  const handleToggleOpen = async () => {
    setTogglingOpen(true);
    try {
      const response = await axios.post(`${API}/restaurant-dashboard/toggle-open`, {});
      setIsOpen(response.data.is_open);
      toast.success(response.data.message);
      // Re-fetch settings to update accepting_orders computed state
      fetchRestaurantStatus();
    } catch {
      toast.error('Erreur lors du changement de statut');
    } finally {
      setTogglingOpen(false);
    }
  };

  const handleTogglePause = async () => {
    setTogglingPause(true);
    const previousState = ordersPaused;
    // Optimistic update
    setOrdersPaused(!previousState);
    try {
      const response = await axios.post(`${API}/restaurant-dashboard/toggle-orders-pause`, {});
      setOrdersPaused(response.data.orders_paused);
      toast.success(response.data.message);
      fetchTodayOrders();
      // Re-fetch settings to update accepting_orders computed state
      fetchRestaurantStatus();
    } catch {
      // Revert on failure
      setOrdersPaused(previousState);
      toast.error('Erreur lors du changement de statut des commandes');
    } finally {
      setTogglingPause(false);
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    setUpdating(orderId);
    try {
      await axios.put(`${API}/restaurant-dashboard/orders/${orderId}/status?status=${encodeURIComponent(newStatus)}`, {});
      toast.success(`Statut mis à jour: ${newStatus}`);
      fetchTodayOrders();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(null);
    }
  };

  const today = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  const pendingOrders = data.orders.filter(o => ['Reçue', 'En préparation', 'Prête'].includes(o.status));

  return (
    <DashboardLayout title="Tableau de bord">
      {/* Onboarding Progress Banner */}
      <OnboardingBanner />

      {/* Restaurant Status Toggle */}
      <div className={`flex items-center justify-between gap-4 mb-4 py-3 px-4 rounded-xl border ${isOpen ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
        <div className="flex items-center gap-3">
          <Store className={`w-5 h-5 ${isOpen ? 'text-emerald-600' : 'text-red-600'}`} />
          <div>
            <p className={`font-medium ${isOpen ? 'text-emerald-700' : 'text-red-700'}`}>
              {isOpen ? 'Restaurant ouvert' : 'Restaurant fermé'}
            </p>
            <p className="text-xs text-gray-500">
              {isOpen ? 'Les clients peuvent commander' : 'Les commandes sont désactivées'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{isOpen ? 'Ouvert' : 'Fermé'}</span>
          <Switch
            checked={isOpen}
            onCheckedChange={handleToggleOpen}
            disabled={togglingOpen}
            data-testid="toggle-open"
          />
        </div>
      </div>

      {/* Orders Pause Toggle */}
      <div className={`flex items-center justify-between gap-4 mb-4 py-3 px-4 rounded-xl border ${!acceptingOrders ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`} data-testid="orders-pause-section">
        <div className="flex items-center gap-3">
          <Package className={`w-5 h-5 ${!acceptingOrders ? 'text-amber-600' : 'text-gray-500'}`} />
          <div>
            <p className={`font-medium text-sm ${!acceptingOrders ? 'text-amber-700' : 'text-gray-700'}`}>
              {acceptingOrders ? 'Commandes acceptées' : 'Commandes bloquées'}
            </p>
            <p className="text-xs text-gray-500">
              {acceptingOrders 
                ? 'Les clients peuvent passer commande' 
                : acceptingOrdersReason || 'Aucune nouvelle commande ne sera acceptée'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {(() => {
            const activeOrders = data.orders.filter(o => ['Reçue', 'Confirmée', 'En préparation'].includes(o.status)).length;
            return activeOrders > 0 ? (
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                activeOrders >= 5 ? 'bg-red-100 text-red-700' : 
                activeOrders >= 3 ? 'bg-amber-100 text-amber-700' : 
                'bg-blue-100 text-blue-700'
              }`} data-testid="active-orders-count">
                {activeOrders} en cours
              </span>
            ) : null;
          })()}
          <Switch
            checked={!ordersPaused}
            onCheckedChange={handleTogglePause}
            disabled={togglingPause}
            data-testid="toggle-orders-pause"
          />
        </div>
      </div>

      {/* Live Indicator */}
      <div className="flex items-center justify-between gap-2 mb-4 py-2 px-4 bg-emerald-50 rounded-xl">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <span className="text-sm font-medium text-emerald-700">En direct</span>
          <span className="text-xs text-emerald-600">
            • Mis à jour {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {newOrdersCount > 0 && (
            <Badge className="bg-orange-100 text-orange-700 animate-pulse">
              {newOrdersCount} en attente
            </Badge>
          )}
          {/* Notifications Toggle */}
          {notificationSupported && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleNotifications}
              className={`gap-2 ${notificationsEnabled ? 'text-emerald-700' : 'text-gray-500'}`}
              title={notificationsEnabled ? 'Notifications activées' : 'Notifications désactivées'}
            >
              {notificationsEnabled ? (
                <Bell className="w-4 h-4" />
              ) : (
                <BellOff className="w-4 h-4" />
              )}
              <span className="hidden sm:inline text-xs">
                {notificationsEnabled ? 'Notifs ON' : 'Notifs OFF'}
              </span>
            </Button>
          )}
        </div>
      </div>

      {/* Low Stock Alert */}
      {showStockAlerts && lowStockDishes.length > 0 && (
        <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <span className="font-medium text-orange-800">
                Alerte stock ({lowStockDishes.length} plat{lowStockDishes.length > 1 ? 's' : ''})
              </span>
            </div>
            <Link to="/restaurant-dashboard/menu">
              <Button variant="outline" size="sm" className="text-orange-700 border-orange-300 hover:bg-orange-100">
                Gérer les stocks
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {lowStockDishes.slice(0, 6).map((dish) => (
              <div 
                key={dish.id} 
                className={`flex items-center gap-2 p-2 rounded-lg ${
                  dish.stock === 0 ? 'bg-red-100' : 'bg-orange-100'
                }`}
              >
                <Package className={`w-4 h-4 ${dish.stock === 0 ? 'text-red-600' : 'text-orange-600'}`} />
                <span className={`text-sm truncate flex-1 ${dish.stock === 0 ? 'text-red-700' : 'text-orange-700'}`}>
                  {dish.name}
                </span>
                <Badge className={`text-xs ${dish.stock === 0 ? 'bg-red-500' : 'bg-orange-500'} text-white`}>
                  {dish.stock === 0 ? 'Épuisé' : `${dish.stock} restant${dish.stock > 1 ? 's' : ''}`}
                </Badge>
              </div>
            ))}
          </div>
          {lowStockDishes.length > 6 && (
            <p className="text-xs text-orange-600 mt-2 text-center">
              + {lowStockDishes.length - 6} autre{lowStockDishes.length - 6 > 1 ? 's' : ''} plat{lowStockDishes.length - 6 > 1 ? 's' : ''} en stock bas
            </p>
          )}
        </div>
      )}

      {/* Date and Refresh */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-gray-500 capitalize">{today}</p>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchTodayOrders}
          className="rounded-full"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Commandes du jour</CardTitle>
            <ShoppingBag className="w-5 h-5 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{data.stats.total_orders}</div>
            <p className="text-xs text-gray-500 mt-1">
              {pendingOrders.length} en cours
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Revenu du jour</CardTitle>
            <Euro className="w-5 h-5 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{data.stats.total_revenue.toFixed(2)} €</div>
          </CardContent>
        </Card>

        <Card className="sm:col-span-2 lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">En attente</CardTitle>
            <Clock className="w-5 h-5 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-500">
              {data.orders.filter(o => o.status === 'Reçue').length}
            </div>
            <p className="text-xs text-gray-500 mt-1">Nouvelles commandes</p>
          </CardContent>
        </Card>
      </div>

      {/* Orders List */}
      <div className="bg-white rounded-2xl border border-gray-200">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Commandes d&apos;aujourd&apos;hui</h2>
          <Link 
            to="/restaurant-dashboard/orders"
            className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
          >
            Voir tout
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement...</div>
        ) : pendingOrders.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <ShoppingBag className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500">Aucune commande en cours</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {pendingOrders.map((order) => (
              <div key={order.id} className="p-4 hover:bg-gray-50">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-semibold text-gray-900">{order.order_number}</span>
                      <span className="text-sm text-gray-600">
                        {order.guest_name || order.client_name || 'Client'}
                      </span>
                      <Badge className={statusColors[order.status]}>
                        {statusLabel(order.status)}
                      </Badge>
                      {order.highlight_tags && order.highlight_tags.map(tag => (
                        <Badge key={tag} className="bg-violet-100 text-violet-700 border-violet-200">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Retrait: {new Date(order.pickup_slot).toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      <span className="font-medium text-gray-900">{order.total_amount.toFixed(2)} €</span>
                    </div>
                    <div className="mt-2">
                      {order.items.map((i, idx) => (
                        <div key={`${i.dish_id || i.dish_name}-${idx}`} className="text-sm text-gray-600">
                          <span>{i.quantity}x {i.dish_name}</span>
                          {i.ingredient_details && i.ingredient_details.length > 0 && (
                            <div className="pl-4 mt-0.5">
                              {i.ingredient_details.map((g, gIdx) => (
                                <span key={g.category || gIdx} className="text-xs text-emerald-600">
                                  {g.category}: {g.items.join(', ')}{gIdx < i.ingredient_details.length - 1 ? ' · ' : ''}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                      {order.special_instructions && (
                        <p className="text-sm text-orange-600 mt-1">
                          Note: {order.special_instructions}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {statusActions[order.status] && (
                    <Button
                      onClick={() => handleUpdateStatus(order.id, nextStatus[order.status])}
                      disabled={updating === order.id}
                      className={`shrink-0 rounded-full ${
                        order.status === 'Reçue' 
                          ? 'bg-blue-500 hover:bg-blue-600' 
                          : order.status === 'En préparation'
                          ? 'bg-emerald-500 hover:bg-emerald-600'
                          : 'bg-gray-500 hover:bg-gray-600'
                      }`}
                      data-testid={`update-status-${order.id}`}
                    >
                      {updating === order.id ? 'Mise à jour...' : statusActions[order.status]}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
